/**
 * Shared dietary option lists and selection rules for the onboarding
 * DietaryRestrictionsStep and the profile DietaryPreference component.
 *
 * Restrictions and nutrition goals both persist into the same
 * familyProfile.dietaryRestrictions array and share one canonical vocabulary
 * with the backend (VALID_DIETARY_RESTRICTIONS) — the split below is purely
 * presentational. Restrictions are factual constraints the user lives with
 * ("I can't / don't eat this"); nutrition goals are aspirational targets
 * (the backend's nutrition-threshold restrictions). Keeping them in separate
 * UI sections stops goal-motivated users from clicking "No restrictions" and
 * never discovering the goal options.
 *
 * NOTE: Numeric nutrition targets (e.g. "40g protein per meal", a calorie
 * budget) are planned as a future layer on top of NUTRITION_GOALS. When that
 * lands, per-goal metadata (nutrient key, unit, default target) should live
 * here so both surfaces stay in sync.
 */

// Factual dietary patterns, diet programs, religious/cultural laws, and
// medical diets. Gated behind "Any dietary restrictions?" in onboarding.
export const DIETARY_RESTRICTIONS = [
  'Vegetarian',
  'Vegan',
  'Pescatarian',
  'Gluten-Free',
  'Dairy-Free',
  'Keto',
  'Paleo',
  'Whole30',
  'Kosher',
  'Halal',
  'Low-FODMAP',
  'Low-Residue',
];

// The backend's nutrition-threshold restrictions, presented as goals.
export const NUTRITION_GOALS = [
  'High-Protein',
  'High-Fiber',
  'Low-Calorie',
  'Low-Carb',
  'Low-Fat',
  'Low-Saturated-Fat',
  'Low-Cholesterol',
  'Low-Sodium',
  'Low-Sugar',
];

// How an allergy selection implies a dietary restriction tag.
//
// `hidden: true` tags (Nut-Free, Soy-Free, Egg-Free) appear in neither UI
// section — they exist purely as a mirror of the allergy checkboxes, so they
// track the allergy in both directions. `hidden: false` tags are also
// checkboxes the user can pick directly, which makes removal asymmetric: see
// syncAllergyRestrictions.
export type AllergyRestrictionRule = {
  /** The dietary restriction tag implied by the allergies below. */
  tag: string;
  /** Any one of these allergies implies the tag. */
  allergies: string[];
  /** Whether the tag is invisible in the dietary-restriction sections. */
  hidden: boolean;
};

export const ALLERGY_RESTRICTION_RULES: AllergyRestrictionRule[] = [
  { tag: 'Nut-Free', allergies: ['Tree Nuts', 'Peanuts'], hidden: true },
  { tag: 'Soy-Free', allergies: ['Soy'], hidden: true },
  { tag: 'Egg-Free', allergies: ['Eggs'], hidden: true },
  { tag: 'Dairy-Free', allergies: ['Milk'], hidden: false },
  { tag: 'Gluten-Free', allergies: ['Wheat'], hidden: false },
];

// Allergen-free tags kept in sync from COMMON_ALLERGIES selections rather than
// picked directly, so they appear in neither UI section above. They are still
// real backend restrictions stored on recipes, so surfaces that filter the
// catalog by restriction (admin Recipe Catalog) need them to offer the full
// vocabulary.
export const ALLERGEN_FREE_TAGS = ALLERGY_RESTRICTION_RULES.filter(
  rule => rule.hidden
).map(rule => rule.tag);

export const COMMON_ALLERGIES = [
  'Milk',
  'Tree Nuts',
  'Peanuts',
  'Shellfish',
  'Fish',
  'Eggs',
  'Soy',
  'Sesame',
  'Wheat',
  'Mustard',
];

// Mutually exclusive selection groups. A tag may appear in several groups
// (Keto conflicts with both fat goals), and a group may span the
// restriction/goal sections (High-Fiber vs Low-Residue) — exclusion always
// evaluates against the combined selection. Low-Fat and Low-Saturated-Fat
// are deliberately NOT exclusive with each other: both can apply at once.
export const MUTUALLY_EXCLUSIVE_GROUPS: string[][] = [
  // Plant-based diet types - only one can be selected
  ['Vegan', 'Vegetarian', 'Pescatarian'],
  // Fiber-based selections - mutually exclusive
  ['High-Fiber', 'Low-Residue'],
  // Fat-based diet approaches - mutually exclusive
  ['Keto', 'Low-Fat'],
  ['Keto', 'Low-Saturated-Fat'],
];

// Explanatory tooltips for options whose plain label is ambiguous or niche
export const RESTRICTION_TOOLTIPS: Record<string, string> = {
  'Low-FODMAP':
    'The elimination phase of the low-FODMAP diet for IBS — avoids onion, garlic, wheat, beans, and certain fruits, vegetables, and dairy',
  'Low-Residue':
    "For digestive conditions like Crohn's disease, IBS, colitis, or diverticulitis",
  'Low-Saturated-Fat':
    'For heart health — limits saturated fat while allowing healthy fats from olive oil, fish, nuts, and avocado',
};

export type NutritionGoalPreset = {
  key: string;
  label: string;
  /** The goal tags the preset checks. Surfaced to the user before applying. */
  goals: string[];
};

// "Quick pick" bundles for the nutrition-goals section, rendered as
// toggleable chips. A chip's on/off state is DERIVED — on iff every goal in
// its bundle is currently checked — never stored, so the checkboxes remain
// the sole source of truth and constituents stay freely uncheckable (a chip
// with a manually unchecked constituent simply reads as off). Toggling on
// checks the bundle through the exclusion-aware logic (so e.g. Keto is
// displaced); toggling off unchecks exactly the bundle's goals.
//
// Bundles MUST stay pairwise disjoint (enforced by test): a shared tag would
// let one chip's toggle silently flip another chip's state. That's also why
// there is no single-tag preset — it would be a synonym for its own checkbox.
//
// Labels are outcome-oriented but deliberately non-clinical ("Blood sugar",
// not "Diabetic-friendly"): condition names imply clinical suitability these
// nutrient bundles don't claim. Digestive diets (Low-Residue, Low-FODMAP)
// are intentionally absent — choosing between them depends on diagnosis, so
// they don't fit a chip.
// Future: when numeric nutrition targets land, presets may also suggest
// per-goal target values.
export const NUTRITION_GOAL_PRESETS: NutritionGoalPreset[] = [
  {
    key: 'heart_health',
    label: 'Heart health',
    goals: ['Low-Saturated-Fat', 'Low-Sodium'],
  },
  {
    key: 'weight_management',
    label: 'Weight management',
    goals: ['Low-Calorie', 'High-Protein'],
  },
  {
    key: 'blood_sugar',
    label: 'Blood sugar',
    goals: ['Low-Sugar', 'Low-Carb'],
  },
];

/**
 * Toggle an option within the combined selection, removing members of any
 * mutually-exclusive group the newly checked option belongs to.
 */
export const toggleDietarySelection = (
  current: string[],
  option: string
): string[] => {
  if (current.includes(option)) {
    return current.filter(r => r !== option);
  }
  const conflictingGroups = MUTUALLY_EXCLUSIVE_GROUPS.filter(group =>
    group.includes(option)
  );
  return [...current, option].filter(
    r => r === option || !conflictingGroups.some(group => group.includes(r))
  );
};

/**
 * Apply a preset's goals to the combined selection: check every goal not
 * already selected, routing each through the exclusion-aware toggle so
 * conflicting selections are displaced. Already-selected goals are left
 * alone (idempotent).
 */
export const applyGoalPreset = (
  current: string[],
  presetGoals: string[]
): string[] => {
  let updated = current;
  for (const goal of presetGoals) {
    if (!updated.includes(goal)) {
      updated = toggleDietarySelection(updated, goal);
    }
  }
  return updated;
};

/**
 * A preset chip's derived on/off state: on iff every goal in its bundle is
 * currently checked.
 */
export const isGoalPresetActive = (
  selected: string[],
  presetGoals: string[]
): boolean => presetGoals.every(goal => selected.includes(goal));

/**
 * Toggle a preset chip. Active → uncheck exactly the bundle's goals;
 * inactive (including partially checked) → complete the bundle via
 * applyGoalPreset.
 */
export const toggleGoalPreset = (
  current: string[],
  presetGoals: string[]
): string[] =>
  isGoalPresetActive(current, presetGoals)
    ? current.filter(r => !presetGoals.includes(r))
    : applyGoalPreset(current, presetGoals);

/**
 * Whether checking `option` would displace an already-selected member of one
 * of its mutually-exclusive groups (used to dim the checkbox).
 */
export const hasExclusionConflict = (
  selected: string[],
  option: string
): boolean =>
  MUTUALLY_EXCLUSIVE_GROUPS.some(
    group =>
      group.includes(option) &&
      group.some(r => r !== option && selected.includes(r))
  );

/**
 * Split the stored combined array into its visible sections. Tags in neither
 * list (hidden allergy-synced tags like Nut-Free/Soy-Free/Egg-Free) belong
 * to neither section and are preserved untouched by section-level clears.
 */
export const partitionDietarySelections = (
  selected: string[]
): { restrictions: string[]; goals: string[] } => ({
  restrictions: selected.filter(r => DIETARY_RESTRICTIONS.includes(r)),
  goals: selected.filter(r => NUTRITION_GOALS.includes(r)),
});

export type DietarySelection = {
  dietaryRestrictions: string[];
  allergies: string[];
};

const impliesTag = (rule: AllergyRestrictionRule, allergies: string[]): boolean =>
  rule.allergies.some(implying =>
    allergies.some(a => a.toLowerCase() === implying.toLowerCase())
  );

/**
 * Reconcile the restriction tags implied by an allergy change.
 *
 * Only tags whose implication *changed* are touched, and removal is
 * asymmetric:
 *
 * - Hidden tags (Nut-Free/Soy-Free/Egg-Free) are a pure mirror of the allergy
 *   checkboxes — nothing else can set them — so they follow the allergy off
 *   as well as on.
 * - Visible tags (Gluten-Free, Dairy-Free) are also restrictions the user can
 *   check deliberately. Unchecking the allergy therefore only removes one
 *   when *this editing session* auto-added it (tracked in `autoAddedTags`);
 *   a Gluten-Free the user picked themselves — or one already on the saved
 *   profile — survives, because silently dropping a restriction the user
 *   never revisits sends them non-compliant recipes.
 *
 * Because it is edge-triggered, an unrelated allergy change can never
 * resurrect a tag the user has since unchecked by hand.
 *
 * `autoAddedTags` is session state: pass the previous value in, store the
 * returned one for the next call. Manually toggling a tag must drop it from
 * that list — see useDietarySelection.
 */
export const syncAllergyRestrictions = (
  current: DietarySelection,
  nextAllergies: string[],
  autoAddedTags: readonly string[] = []
): { value: DietarySelection; autoAddedTags: string[] } => {
  let restrictions = [...current.dietaryRestrictions];
  const autoAdded = new Set(autoAddedTags);

  for (const rule of ALLERGY_RESTRICTION_RULES) {
    const impliedBefore = impliesTag(rule, current.allergies);
    const impliedNow = impliesTag(rule, nextAllergies);
    if (impliedBefore === impliedNow) continue;

    if (impliedNow) {
      if (!restrictions.includes(rule.tag)) {
        restrictions.push(rule.tag);
        // A tag that was already checked stays the user's, so it is not
        // recorded here and will not be auto-removed later.
        if (!rule.hidden) autoAdded.add(rule.tag);
      }
    } else {
      if (rule.hidden || autoAdded.has(rule.tag)) {
        restrictions = restrictions.filter(r => r !== rule.tag);
      }
      autoAdded.delete(rule.tag);
    }
  }

  return {
    value: { dietaryRestrictions: restrictions, allergies: nextAllergies },
    autoAddedTags: Array.from(autoAdded),
  };
};

/**
 * Parse the free-text allergy input (comma-separated) into new entries,
 * dropping blanks and anything already selected (case-insensitively, so
 * "wheat" can't be added alongside "Wheat" and dodge the sync rules).
 */
export const parseAllergyInput = (
  text: string,
  existing: string[]
): string[] => {
  const added: string[] = [];
  for (const item of text.split(',').map(part => part.trim())) {
    if (!item) continue;
    const isDuplicate = [...existing, ...added].some(
      a => a.toLowerCase() === item.toLowerCase()
    );
    if (!isDuplicate) added.push(item);
  }
  return added;
};
